import { useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';

/**
 * Hook para gerenciar dados de reservatórios com polling automático
 * Encapsula lógica complexa: AbortController, RequestId, Timeout, Polling
 * 
 * @param {number|string} selectedBloco - ID do bloco selecionado
 * @param {number} activeCondominio - ID do condomínio ativo
 * @param {string} apiUrl - URL base da API
 * @returns {Object} { reservatorios, isLoading, error }
 */
const useReservatorioFetch = (selectedBloco, activeCondominio, apiUrl) => {
  // Estados
  const [reservatorios, setReservatorios] = useState([]);
  const [resumo, setResumo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Refs para AbortController, RequestId e valores atuais (evita deps infinitas)
  const abortControllerRef = useRef(null);
  const requestIdRef = useRef(0);
  const cancelReasonRef = useRef(null);
  const currentBlocoRef = useRef(selectedBloco);
  const currentCondominioRef = useRef(activeCondominio);

  // Sincronizar refs com estado atual
  useEffect(() => {
    currentBlocoRef.current = selectedBloco;
  }, [selectedBloco]);

  useEffect(() => {
    currentCondominioRef.current = activeCondominio;
  }, [activeCondominio]);

  // Função para buscar reservatórios
  const fetchReservatorios = useCallback(async () => {
    if (!currentBlocoRef.current) return;

    // Gerar ID único para esta requisição (para ignorar responses obsoletas)
    const requestId = ++requestIdRef.current;

    // Cancelar request anterior
    if (abortControllerRef.current) {
      cancelReasonRef.current = 'nova-requisicao';
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setReservatorios([]); // ✨ Limpar dados anteriores imediatamente
    setResumo(null);
    setIsLoading(true);
    setError(null);

    try {
      // Função helper: criar Promise com timeout
      const fetchWithTimeout = async (promise, timeoutMs = 10000) => {
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error(`Timeout após ${timeoutMs}ms`)), timeoutMs)
        );
        return Promise.race([promise, timeoutPromise]);
      };

      // Fetch reservatorios, resumo e sparklines em paralelo com timeout de 10s e AbortController
      const reqHeaders = { 'x-condominio-id': currentCondominioRef.current };
      const reqConfig = { headers: reqHeaders, signal: abortControllerRef.current.signal };
      
      const response = await fetchWithTimeout(
        Promise.all([
          axios.get(`${apiUrl}/api/reservatorios/blocos/${currentBlocoRef.current}`, reqConfig),
          axios.get(`${apiUrl}/api/reservatorios/blocos/${currentBlocoRef.current}/resumo`, reqConfig),
          axios.get(`${apiUrl}/api/reservatorios/blocos/${currentBlocoRef.current}/sparklines`, reqConfig)
        ]),
        10000
      );

      // ✅ Validar se este resultado ainda é relevante (ignorar responses obsoletas)
      if (requestId !== requestIdRef.current) {
        console.log(`[useReservatorioFetch] Request ${requestId} ignorado (nova requisição iniciada)`);
        return;
      }

      const resReservatorios = response[0].data.dados || [];
      const resResumo = response[1].data.dados || null;
      const resSparklines = response[2].data.dados || {};

      const updatedReservatorios = Array.isArray(resReservatorios) ? resReservatorios.flatMap(modulo => {
        if (!modulo.channels || !Array.isArray(modulo.channels)) {
          console.error('Módulo inválido ou sem canais:', modulo);
          return [];
        }
        return modulo.channels.map(channel => {
          const reservatorioId = channel.reservatorio_id ? String(channel.reservatorio_id) : `${modulo.id}-${channel.channelnum}`;
          return {
            id: reservatorioId,
            modulo_id: modulo.id,
            channelnum: channel.channelnum,
            numero_reservatorio: channel.numero_reservatorio,
            nome: channel.numero_reservatorio, // Removido o prefixo duplicado
            capacidade_litros: channel.capacidade_litros,
            tipo: channel.tipo,
            marca: channel.marca,
            modelo: channel.modelo,
            tendencia: channel.tendencia,
            ultimaLeitura: channel.ultimaLeitura,
            nivel_minimo: channel.nivel_minimo,
            nivel_maximo: channel.nivel_maximo,
            nivelPorcentagem: channel.nivelPorcentagem ?? 'Indefinido',
            nivelLitros: channel.nivelLitros ?? 'Indefinido',
            status: channel.status || 'normal',
            grupo_interconectado: channel.grupo_interconectado,
            sparkline: resSparklines[reservatorioId] || []
          };
        });
      }) : [];

      setReservatorios(updatedReservatorios);
      setResumo(resResumo);
      setError(null);
    } catch (err) {
      // ✅ Ignorar se foi cancelado (AbortError ou CanceledError do Axios)
      if (err.name === 'AbortError' || err.name === 'CanceledError' || err.code === 'ABORT_ERR' || err.code === 'ERR_CANCELED') {
        if (cancelReasonRef.current && cancelReasonRef.current !== 'unmount') {
          console.log(`[useReservatorioFetch] Request cancelado (${cancelReasonRef.current})`);
        }
        return;
      }

      // Ignorar se foi timeout ou já foi substituído por outro request
      if (requestId !== requestIdRef.current) {
        return;
      }

      console.error('[useReservatorioFetch] Erro ao buscar reservatorios:', err);
      setError('Erro ao buscar informações de nível para os reservatórios.');
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoading(false);
        cancelReasonRef.current = null;
      }
    }
  }, [apiUrl]);

  // Função para limpar erro
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Polling automático: atualiza reservatórios quando bloco muda ou a cada 5 minutos
  useEffect(() => {
    if (!selectedBloco) {
      setReservatorios([]);
      return;
    }

    // Disparar fetch imediatamente quando bloco muda
    fetchReservatorios();

    // Atualizar a cada 5 minutos usando refs para ter sempre valores atuais
    const interval = setInterval(() => {
      if (currentBlocoRef.current) {
        fetchReservatorios();
      }
    }, 300000); // 5 minutos

    // Cleanup: parar interval e cancelar requisição pendente ao desmontar ou trocar bloco
    return () => {
      clearInterval(interval);
      if (abortControllerRef.current) {
        cancelReasonRef.current = 'unmount';
        abortControllerRef.current.abort();
      }
    };
  }, [selectedBloco, fetchReservatorios]);

  return {
    reservatorios,
    resumo,
    isLoading,
    error,
    refetch: fetchReservatorios,  // ✨ Expor função de refetch
    clearError  // ✨ Expor função para limpar erro
  };
};

export default useReservatorioFetch;
